package net.frozenbit.games.soundgame.viewmodels

import androidx.compose.foundation.text.input.InputTransformation
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.foundation.text.input.allCaps
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import androidx.compose.ui.text.intl.Locale
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import io.ktor.client.plugins.websocket.*
import kotlinx.coroutines.Job
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import net.frozenbit.games.soundgame.KeyValueStore
import net.frozenbit.games.soundgame.dtos.CommonControllerGameData
import net.frozenbit.games.soundgame.dtos.ControllerClientMessage
import net.frozenbit.games.soundgame.dtos.ControllerDisconnect
import net.frozenbit.games.soundgame.dtos.ControllerGameOverUpdate
import net.frozenbit.games.soundgame.dtos.ControllerWaitUpdate
import net.frozenbit.games.soundgame.dtos.ControllerLobbyUpdate
import net.frozenbit.games.soundgame.dtos.ControllerPromptSelectionUpdate
import net.frozenbit.games.soundgame.dtos.ControllerIdleUpdate
import net.frozenbit.games.soundgame.dtos.ControllerServerMessage
import net.frozenbit.games.soundgame.dtos.ControllerSoundSelectionUpdate
import net.frozenbit.games.soundgame.dtos.ControllerVotingUpdate
import net.frozenbit.games.soundgame.dtos.GameOverActionSelected
import net.frozenbit.games.soundgame.dtos.JoinGame
import net.frozenbit.games.soundgame.dtos.ReplaySound
import net.frozenbit.games.soundgame.dtos.SelectPrompt
import net.frozenbit.games.soundgame.dtos.SelectSound
import net.frozenbit.games.soundgame.dtos.StartGame
import net.frozenbit.games.soundgame.dtos.UpdatePlayerInfo
import net.frozenbit.games.soundgame.dtos.VoteForCreation
import net.frozenbit.games.soundgame.utils.Countdown
import net.frozenbit.games.soundgame.utils.TimeCorrection
import net.frozenbit.games.soundgame.utils.Timeout
import net.frozenbit.games.soundgame.utils.createClient
import net.frozenbit.games.soundgame.utils.permanentSocketSession
import net.frozenbit.games.soundgame.viewmodels.ControllerViewModel.ControllerUiState.*
import net.frozenbit.games.soundgame.viewmodels.ControllerViewModel.ControllerUiState.SoundSelection.*
import net.frozenbit.games.soundgame.viewmodels.ControllerViewModel.ControllerUiState.Voting.*
import net.frozenbit.games.soundgame.viewmodels.deeplink.joinCodeFromUrl
import kotlin.time.Duration.Companion.minutes

class ControllerViewModel(
    private val keyValueStore: KeyValueStore,
) : ViewModel() {
    private val _uiState = mutableStateOf<ControllerUiState>(ControllerUiState.Idle(loading = false))
    val uiState: State<ControllerUiState> = _uiState

    val playerNameInput = TextFieldState(
        initialText = keyValueStore.get("controller.playerName") ?: "",
    )
    val joinCodeInput = TextFieldState(
        initialText = joinCodeFromUrl()
            ?: keyValueStore.get("controller.joinCode")
            ?: "",
    )
    val transformJoinCodeInput = InputTransformation.allCaps(Locale.current)

    private val _selectedAvatarIndex = mutableStateOf<Int?>(null)
    val selectedAvatarIndex: State<Int?> = _selectedAvatarIndex

    private var currentGameCode: String? = null
    private var currentSessionId: String? = null

    private val timeCorrection = TimeCorrection()

    private val nextPhaseCountdown = Countdown()

    val countdownTicks = nextPhaseCountdown.tickFlow()

    private var gameLoop: Job = viewModelScope.launch {
        _uiState.value = Idle(loading = true)
        val client = createClient()
        client.permanentSocketSession(
            url = "controller",
            onReconnected = {
                currentGameCode?.let { joinCode ->
                    currentSessionId?.let { sessionId ->
                        _uiState.value = Idle(loading = true)
                        sendToServer(
                            JoinGame(
                                sessionId = sessionId,
                                joinCode = joinCode,
                                playerName = playerNameInput.text.toString(),
                            )
                        )
                    }
                }
            },
        ) {
            val sendJob = launch { _outgoingMessages.collect { sendToServer(it) } }
            while (currentCoroutineContext().isActive) {
                val serverMessage = try {
                    receiveDeserialized<ControllerServerMessage>()
                } catch (e: Exception) {
                    println("Failed to deserialize message from controller: $e")
                    break
                }
                when (serverMessage) {
                    ControllerIdleUpdate -> {
                        _uiState.value = Idle(loading = false)
                    }

                    is ControllerDisconnect -> {
                        _uiState.value = Idle(loading = false, errorMessage = serverMessage.reason)
                    }

                    is ControllerLobbyUpdate -> {
                        keyValueStore.set(
                            "controller.sessionId",
                            serverMessage.common.sessionId,
                        )
                        updateData(serverMessage.common)
                        _selectedAvatarIndex.value = serverMessage.currentAvatarIndex
                        _uiState.value = Lobby(
                            showStartButton = serverMessage.showAdminControls,
                            enableStartButton = serverMessage.enableStartButton,
                        )
                    }

                    is ControllerPromptSelectionUpdate -> {
                        updateData(serverMessage.common)
                        val actualStartTime = timeCorrection.actual(serverMessage.startedAt)
                        val actualEndTime = timeCorrection.actual(serverMessage.expiresAt)
                        nextPhaseCountdown.start(start = actualStartTime, target = actualEndTime)
                        _uiState.value = PromptSelection(
                            optionTexts = serverMessage.optionTexts,
                        )
                    }

                    is ControllerWaitUpdate -> {
                        updateData(serverMessage.common)
                        _uiState.value = Waiting
                    }

                    is ControllerSoundSelectionUpdate -> {
                        updateData(serverMessage.common)
                        val actualStartTime = timeCorrection.actual(serverMessage.startedAt)
                        val actualEndTime = timeCorrection.actual(serverMessage.expiresAt)
                        nextPhaseCountdown.start(start = actualStartTime, target = actualEndTime)
                        _uiState.value = SoundSelection(
                            soundOptions = serverMessage
                                .optionTexts.mapIndexed { index, text ->
                                    SoundOption(
                                        text = text,
                                        isSelected = serverMessage.picks.contains(index),
                                        soundNumber = serverMessage.picks
                                            .indexOf(index)
                                            .takeIf { it >= 0 }
                                            ?.plus(1),
                                    )
                                },
                            lockInTimeout = serverMessage.lockIn?.let { lockIn ->
                                Timeout.from(lockIn, timeCorrection)
                            },
                        )
                    }

                    is ControllerVotingUpdate -> {
                        updateData(serverMessage.common)
                        val actualStartTime = timeCorrection.actual(serverMessage.startedAt)
                        val actualEndTime = timeCorrection.actual(serverMessage.expiresAt)
                        nextPhaseCountdown.start(start = actualStartTime, target = actualEndTime)
                        _uiState.value = Voting(
                            voteOptions = serverMessage.options.map {
                                VoteOption(
                                    line1 = it.sound1Title,
                                    line2 = it.sound2Title,
                                )
                            }
                        )
                    }

                    is ControllerGameOverUpdate -> {
                        updateData(serverMessage.common)
                        _uiState.value = GameOver(
                            actionTexts = serverMessage.buttons,
                        )
                    }
                }
            }
            sendJob.cancel()
        }
        client.close()
    }

    private fun updateData(common: CommonControllerGameData) {
        currentSessionId = common.sessionId
        timeCorrection.calibrate(common.serverTime)
    }

    private suspend fun DefaultClientWebSocketSession.sendToServer(message: ControllerClientMessage) {
        sendSerialized(message)
    }

    private val _outgoingMessages = MutableSharedFlow<ControllerClientMessage>(
        replay = 0,
        extraBufferCapacity = 10,
    )

    fun start() {
        _uiState.value = Idle(loading = true)
        val playerName = playerNameInput.text.toString()
        val joinCode = joinCodeInput.text.toString().trim().uppercase()
        keyValueStore.set(
            "controller.playerName",
            playerName,
        )
        keyValueStore.set(
            "controller.joinCode",
            joinCode,
            maxLifetime = 5.minutes,
        )
        if (joinCode.isBlank()) {
            _uiState.value = Idle(
                loading = false,
                errorMessage = "Bitte gib den dreistelligen Spielcode ein.",
            )
            return
        }
        if (playerName.isBlank()) {
            _uiState.value = Idle(
                loading = false,
                errorMessage = "Bitte gib einen Spielernamen ein.",
            )
            return
        }
        currentGameCode = joinCode
        _outgoingMessages.tryEmit(
            JoinGame(
                sessionId = keyValueStore.get("controller.sessionId"),
                joinCode = joinCode,
                playerName = playerName,
                avatarIndex = _selectedAvatarIndex.value,
            )
        )
    }

    fun startGame() {
        _outgoingMessages.tryEmit(StartGame)
    }

    fun selectAvatar(index: Int) {
        _selectedAvatarIndex.value = index
        _outgoingMessages.tryEmit(
            UpdatePlayerInfo(
                avatarIndex = index,
            )
        )
    }

    fun selectPrompt(index: Int) {
        _outgoingMessages.tryEmit(
            SelectPrompt(
                selectedIndex = index,
            )
        )
    }

    fun selectSound(index: Int) {
        _outgoingMessages.tryEmit(
            SelectSound(
                selectedIndex = index,
            )
        )
    }

    fun selectVote(index: Int) {
        _outgoingMessages.tryEmit(
            VoteForCreation(
                selectedIndex = index,
            )
        )
    }

    fun replaySound(index: Int) {
        _outgoingMessages.tryEmit(
            ReplaySound(
                soundIndex = index,
            )
        )
    }

    fun selectGameOverAction(index: Int) {
        _outgoingMessages.tryEmit(
            GameOverActionSelected(
                selectedIndex = index,
            )
        )
    }

    sealed class ControllerUiState {
        data class Idle(
            val loading: Boolean,
            val errorMessage: String? = null,
        ) : ControllerUiState()

        data class Lobby(
            val showStartButton: Boolean,
            val enableStartButton: Boolean,
        ) : ControllerUiState()

        data object Waiting : ControllerUiState()

        data class PromptSelection(
            val optionTexts: List<String>,
        ) : ControllerUiState()

        data class SoundSelection(
            val soundOptions: List<SoundOption>,
            val lockInTimeout: Timeout?,
        ) : ControllerUiState() {
            data class SoundOption(
                val text: String,
                val isSelected: Boolean,
                val soundNumber: Int?,
            )
        }

        data class Voting(
            val voteOptions: List<VoteOption>,
        ) : ControllerUiState() {
            data class VoteOption(
                val line1: String,
                val line2: String,
            )
        }

        data class GameOver(
            val actionTexts: List<String>,
        ) : ControllerUiState()
    }

    class PlayerRow(
        val name: String,
    )
}
